import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
// import { io, Socket } from 'socket.io-client';
import URLImport from '../common/URLImport';
import {
  Container,
  Header,
  Section,
  Grid,
  FlexBox
} from '../ui/layout';
import {
  Button,
  FormGroup,
  FormLabel,
  SearchInput
} from '../ui/form';
import {
  Card,
  StatCard,
  StatNumber,
  StatLabel,
  Badge,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner
} from '../ui/data-display';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ProgressContainer,
  ProgressTrack,
  ProgressFill,
  ProgressLabel
} from '../ui/feedback';
import {
  Title,
  Text
} from '../ui/common';
import {
  Pagination,
  PaginationButton,
  PaginationInfo,
  ModernTabsContainer,
  ModernTab
} from '../ui/navigation';

interface Vacancy {
  _id?: string;
  role: string;
  createdAt: string;
  previous_contact_id?: string | { _id: string; first_name: string; last_name: string };
  sales_rep_id?: string;
}

interface Company {
  _id: string;
  name: string;
  headquarters?: string;
  website?: string;
  industry?: string;
  vacancies: Vacancy[];
  created_at: string;
  updated_at: string;
}

interface Contact {
  _id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  role?: string;
  location?: string;
  company_id?: {
    _id: string;
    name: string;
  };
  sales_rep_id?: string | null;
  sales_rep?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  status_outcome?: string;
  created_at: string;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

const VacanciesManagement: React.FC = () => {
  const { hasRole } = useAuth();
  const location = useLocation();

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    const params = new URLSearchParams(location.search);
    return params.get(key) || defaultVal;
  };

  const [companies, setCompanies] = useState<Company[]>([]);
  const [unassignedContacts, setUnassignedContacts] = useState<Contact[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => getInitialParam('search'));
  const [showOnlyWithVacancies, setShowOnlyWithVacancies] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(getInitialParam('page', '1'), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ totalVacancies: 0, companiesWithVacancies: 0 });
  const [activeTab, setActiveTab] = useState<'vacancies' | 'unassigned'>(() => {
    const tab = getInitialParam('tab');
    return tab === 'unassigned' ? 'unassigned' : 'vacancies';
  });
  const pendingScrollY = useRef<number | null>(null);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  // const [showCreateContactModal, setShowCreateContactModal] = useState(false);
  const [showSalesRepModal, setShowSalesRepModal] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState<{ company: Company; vacancy: Vacancy } | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  
  // Form states
  const [profileUrl, setProfileUrl] = useState('');
  const [selectedSalesRep, setSelectedSalesRep] = useState<User | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [salesRepSearch, setSalesRepSearch] = useState('');
  
  // Scraper states
  const [scraperStatus, setScraperStatus] = useState({
    isRunning: false,
    isConnected: false,
    currentPage: null as any,
    currentContactData: null as any,
    processedCount: 0,
    totalCount: 0
  });
  const [showScraperProgress, setShowScraperProgress] = useState(false);
  // const socketRef = useRef<Socket | null>(null);
  
  const pageSize = 10;
  const navigate = useNavigate();

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (activeTab !== 'vacancies') params.set('tab', activeTab);
    if (currentPage > 1) params.set('page', currentPage.toString());

    navigate(`?${params.toString()}`, { replace: true });
  }, [searchTerm, activeTab, currentPage, navigate]);

  const canViewUnassigned = hasRole(['admin', 'super_user']);
  const canDelete = hasRole(['admin']);

  const handleDeleteVacancy = async (companyId: string, vacancyId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa posizione?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/dev/vacancies/${companyId}/${vacancyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVacancies();
    } catch (err) {
      console.error(err);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleDeleteAllVacancies = async () => {
    if (!window.confirm('ATTENZIONE: Stai per eliminare TUTTE le posizioni da TUTTE le aziende. QUESTA AZIONE È IRREVERSIBILE. Continuare?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/dev/vacancies/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVacancies();
    } catch (err) {
      console.error(err);
      alert('Errore durante l\'eliminazione');
    }
  };

  useEffect(() => {
    if (activeTab === 'vacancies') {
      fetchVacancies();
    } else if (canViewUnassigned) {
      fetchUnassignedContacts();
    } else {
      setActiveTab('vacancies');
    }
    fetchUsers();
  }, [currentPage, searchTerm, showOnlyWithVacancies, activeTab, canViewUnassigned]);

  // Restore scroll position from sessionStorage (when returning from detail page)
  useEffect(() => {
    const saved = sessionStorage.getItem('vacanciesScrollY');
    if (saved) {
      pendingScrollY.current = parseInt(saved, 10);
      sessionStorage.removeItem('vacanciesScrollY');
    }
  }, []);

  useEffect(() => {
    if (!loading && pendingScrollY.current !== null) {
      window.scrollTo({ top: pendingScrollY.current, behavior: 'auto' });
      pendingScrollY.current = null;
    }
  }, [loading]);

  // const setupWebSocketConnection = () => {
  //   if (socketRef.current) return; // Already connected
    
  //   try {
  //     const API_BASE_URL = import.meta.env.VITE_API_URL || window.location.origin;
  //     socketRef.current = io(API_BASE_URL, {
  //       transports: ['websocket', 'polling']
  //     });

  //     socketRef.current.on('connect', () => {
  //       console.log('Connected to scraper WebSocket');
  //       setScraperStatus(prev => ({ ...prev, isConnected: true }));
  //     });

  //     socketRef.current.on('disconnect', () => {
  //       console.log('Disconnected from scraper WebSocket');
  //       setScraperStatus(prev => ({ ...prev, isConnected: false }));
  //     });

  //     socketRef.current.on('scraper-status', (data) => {
  //       console.log('Scraper status update:', data);
  //       setScraperStatus(prev => ({
  //         ...prev,
  //         isRunning: data.isRunning,
  //         currentPage: data.currentPage,
  //         currentContactData: data.currentContactData,
  //         processedCount: data.processedCount || 0,
  //         totalCount: data.totalCount || 0
  //       }));
  //     });

  //     socketRef.current.on('scraper-complete', () => {
  //       console.log('Scraper completed');
  //       setScraperStatus(prev => ({ ...prev, isRunning: false }));
  //       // Refresh data
  //       if (activeTab === 'vacancies') {
  //         fetchVacancies();
  //       } else {
  //         fetchUnassignedContacts();
  //       }
  //     });

  //     socketRef.current.on('scraper-error', (error) => {
  //       console.error('Scraper error:', error);
  //       setScraperStatus(prev => ({ ...prev, isRunning: false }));
  //       alert('Scraper error: ' + error.message);
  //     });

  //   } catch (error) {
  //     console.error('Failed to setup WebSocket connection:', error);
  //   }
  // };

  const fetchVacancies = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/companies/vacancies', {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          hasVacancies: true
        }
      });
      
      // The API returns { success: true, data: { companies, pagination, stats } }
      const companiesData = response.data?.data?.companies;
      const paginationData = response.data?.data?.pagination;
      const statsData = response.data?.data?.stats;
      
      setCompanies(companiesData || []);
      setTotalPages(paginationData?.totalPages || 1);
      setStats({
        totalVacancies: statsData?.totalVacancies || 0,
        companiesWithVacancies: statsData?.companiesWithVacancies || 0
      });
    } catch (error) {
      console.error('Error fetching vacancies:', error);
      // Set safe defaults on error
      setCompanies([]);
      setTotalPages(1);
      setStats({ totalVacancies: 0, companiesWithVacancies: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnassignedContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/contacts', {
        params: {
          page: currentPage,
          limit: pageSize,
          search: searchTerm,
          unassigned: 'true'
        }
      });
      
      // The API returns { success: true, data: { contacts, pagination } }
      const contactsData = response.data?.data?.contacts;
      const paginationData = response.data?.data?.pagination;
      
      setUnassignedContacts(Array.isArray(contactsData) ? contactsData : []);
      setTotalPages(paginationData?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching unassigned contacts:', error);
      // Set safe defaults on error
      setUnassignedContacts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      // The API returns { success: true, data: { users: [...] } }
      // Ensure we always set an array, even if the API returns something unexpected
      const usersData = response.data?.data?.users;
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Set empty array on error to prevent filter issues
      setUsers([]);
    }
  };

  const handleAssignVacancy = (company: Company, vacancy: Vacancy) => {
    setSelectedVacancy({ company, vacancy });
    setShowAssignModal(true);
  };

  const handleAssignSalesRep = (contact: Contact) => {
    setSelectedContact(contact);
    setShowSalesRepModal(true);
  };

  // const handleCreateContact = async () => {
  //   if (!selectedVacancy || !importType) return;

  //   try {
  //     setIsProcessing(true);
      
  //     if (importType === 'url' && profileUrl) {
  //       // Setup WebSocket connection before starting scraper
  //       setupWebSocketConnection();
        
  //       const response = await axios.post('/api/contacts/scrape-profile', {
  //         profileUrl,
  //         companyId: selectedVacancy.company._id,
  //         vacancyId: selectedVacancy.vacancy._id
  //       });

  //       if (response.data.success) {
  //         setShowScraperProgress(true);
  //         setShowAssignModal(false);
  //       } else {
  //         alert('Failed to start profile scraping');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error creating contact:', error);
  //     alert('Error creating contact');
  //   } finally {
  //     setIsProcessing(false);
  //   }
  // };

  const handleAssignSalesRepToContact = async () => {
    if (!selectedContact || !selectedSalesRep) return;

    try {
      await axios.put(`/api/contacts/${selectedContact._id}`, {
        first_name: selectedContact.first_name,
        last_name: selectedContact.last_name,
        email: selectedContact.email ?? null,
        phone: selectedContact.phone ?? null,
        role: selectedContact.role ?? null,
        location: selectedContact.location ?? null,
        company_id: (typeof selectedContact.company_id === 'object'
          ? selectedContact.company_id?._id
          : selectedContact.company_id) ?? null,
        sales_rep_id: selectedSalesRep._id,
      });
      
      setShowSalesRepModal(false);
      setSelectedContact(null);
      setSelectedSalesRep(null);
      
      // Refresh data
      if (activeTab === 'unassigned') {
        fetchUnassignedContacts();
      }
    } catch (error) {
      console.error('Error assigning sales rep:', error);
      alert('Error assigning sales rep');
    }
  };

  const stopScraper = async () => {
    try {
      await axios.post('/api/contacts/stop-scraper');
      setScraperStatus(prev => ({ ...prev, isRunning: false }));
    } catch (error) {
      console.error('Error stopping scraper:', error);
    }
  };

  const navigateWithScroll = (path: string) => {
    sessionStorage.setItem('vacanciesScrollY', String(window.scrollY));
    navigate(path);
  };

  const filteredUsers = (users || []).filter(u =>
    u.firstName.toLowerCase().includes(salesRepSearch.toLowerCase()) ||
    u.lastName.toLowerCase().includes(salesRepSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(salesRepSearch.toLowerCase())
  );

  const handlePageChange = (nextPage: number) => {
    pendingScrollY.current = window.scrollY;
    setCurrentPage(nextPage);
  };

  const renderVacanciesTab = () => (
    <>
      <Grid columns="repeat(auto-fit, minmax(200px, 1fr))" gap="1rem" marginBottom="2rem">
        <StatCard>
          <StatNumber>{stats.totalVacancies}</StatNumber>
          <StatLabel>Totale Posizioni Libere</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.companiesWithVacancies}</StatNumber>
          <StatLabel>Aziende con Posizioni Libere</StatLabel>
        </StatCard>
      </Grid>

      <FlexBox gap="1rem" margin="0 0 2rem 0" wrap="wrap">
        <SearchInput
          placeholder="Cerca aziende..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          style={{ flex: 1, minWidth: '250px' }}
        />
        {canDelete && (
          <Button
            variant="danger"
            onClick={handleDeleteAllVacancies}
          >
            Elimina Tutte
          </Button>
        )}
        <Button
          variant={showOnlyWithVacancies ? 'primary' : 'secondary'}
          onClick={() => setShowOnlyWithVacancies(!showOnlyWithVacancies)}
          style={{ display: 'none' }} // Hide this button since we always show only companies with vacancies
        >
          {showOnlyWithVacancies ? 'Mostra Tutte le Aziende' : 'Solo con Posizioni Libere'}
        </Button>
      </FlexBox>

      {loading ? (
        <FlexBox justifyContent="center" padding="3rem">
          <LoadingSpinner />
        </FlexBox>
      ) : companies.length === 0 || (companies || []).filter(company => (company.vacancies || []).length > 0).length === 0 ? (
        <EmptyState padding="3rem" textAlign="center">
          <EmptyStateTitle>Nessuna azienda con posizioni libere trovata</EmptyStateTitle>
          <EmptyStateText>Nessuna azienda ha posizioni aperte al momento</EmptyStateText>
        </EmptyState>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
          {(companies || [])
            .filter(company => (company.vacancies || []).length > 0) // Only show companies with vacancies
            .map((company) => (
            <Card key={company._id} padding="1.5rem">
              <FlexBox justifyContent="space-between" alignItems="flex-start" margin="0 0 1rem 0">
                <div style={{ flex: 1 }}>
                  <Title level={3} margin="0 0 0.5rem 0">
                    {company.name}
                  </Title>
                  <Text variant="caption" color="#6b7280">
                    {company.headquarters && `📍 ${company.headquarters}`}
                    {company.industry && ` • ${company.industry}`}
                    {company.website && (
                      <>
                        {' • '}
                        <a href={company.website} target="_blank" rel="noopener noreferrer">
                          {company.website}
                        </a>
                      </>
                    )}
                  </Text>
                </div>
                <Badge variant="negativo">
                  {(company.vacancies || []).length} posizioni
                </Badge>
              </FlexBox>

              <Section margin="1rem 0 0 0" padding="1.5rem 0 0 0" style={{ borderTop: '1px solid #e1e8ed' }}>
                <Title level={4} margin="0 0 1.25rem 0" style={{ padding: '0 1rem', marginLeft: '0.5rem' }}>
                  Posizioni Aperte ({(company.vacancies || []).length})
                </Title>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 1rem', marginLeft: '0.5rem' }}>
                  {(company.vacancies || []).map((vacancy) => (
                    <Card key={`${company._id}-${vacancy.role}-${vacancy.createdAt}`} padding="1.5rem" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <FlexBox justifyContent="space-between" alignItems="flex-start" style={{ gap: '20px' }}>
                        <div style={{ flex: 1, paddingRight: '1rem' }}>
                          <Text variant="body1" style={{ fontWeight: '600', fontSize: '1.1rem', color: '#1e293b', display: 'block' }}>
                            {vacancy.role}
                          </Text>
                          {vacancy.previous_contact_id && typeof vacancy.previous_contact_id === 'object' && (
                            <Text variant="body2" style={{ marginTop: '0.5rem', color: '#334155', display: 'block' }}>
                              Precedentemente ricoperto da: {' '}
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const id = (vacancy.previous_contact_id as any)._id;
                                  if (id) navigateWithScroll(`/dashboard/contacts/${id}`);
                                }}
                                style={{ color: '#0ea5e9', textDecoration: 'none' }}
                              >
                                {(vacancy.previous_contact_id as any).first_name} {(vacancy.previous_contact_id as any).last_name}
                              </a>
                            </Text>
                          )}
                          <Text variant="caption" color="#64748b" style={{ lineHeight: 1.6, marginTop: '0.5rem', display: 'block' }}>
                            Scoperta il: {new Date(vacancy.createdAt).toLocaleDateString()}
                          </Text>
                        </div>
                        <FlexBox gap="0.5rem">
                          <Button
                            size="small"
                            variant="success"
                            onClick={() => handleAssignVacancy(company, vacancy)}
                            style={{ minWidth: '80px' }}
                          >
                            Assegna
                          </Button>
                          {canDelete && vacancy._id && (
                            <Button
                              size="small"
                              variant="danger"
                              onClick={() => handleDeleteVacancy(company._id, vacancy._id!)}
                            >
                              Elimina
                            </Button>
                          )}
                        </FlexBox>
                      </FlexBox>
                    </Card>
                  ))}
                </div>
              </Section>
            </Card>
          ))}
        </div>
      )}
    </>
  );

  const renderUnassignedTab = () => (
    <>
      <FlexBox gap="1rem" margin="0 0 2rem 0">
        <SearchInput
          placeholder="Cerca contatti..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          style={{ flex: 1 }}
        />
      </FlexBox>

      {loading ? (
        <FlexBox justifyContent="center" padding="3rem">
          <LoadingSpinner />
        </FlexBox>
      ) : (unassignedContacts || []).length === 0 ? (
        <EmptyState padding="3rem" textAlign="center">
          <EmptyStateTitle>Nessun Contatto Non Assegnato</EmptyStateTitle>
          <EmptyStateText>Tutti i contatti sono stati assegnati ai commerciali</EmptyStateText>
        </EmptyState>
      ) : (
        <Grid columns="repeat(auto-fill, minmax(300px, 1fr))" gap="1rem">
          {(unassignedContacts || []).map((contact) => (
            <Card key={contact._id} padding="1rem">
              <div style={{ marginBottom: '0.75rem' }}>
                <Title level={4} margin="0 0 0.25rem 0">
                  {contact.first_name} {contact.last_name}
                </Title>
                <Text variant="body2" color="#6b7280">
                  {contact.email && `📧 ${contact.email}`}
                  {contact.phone && ` • 📱 ${contact.phone}`}
                  {contact.role && ` • ${contact.role}`}
                  {contact.location && ` • 📍 ${contact.location}`}
                  {contact.company_id && ` • ${contact.company_id.name}`}
                </Text>
              </div>
              <Button
                variant="primary"
                size="small"
                fullWidth
                onClick={() => handleAssignSalesRep(contact)}
              >
                Assegna Commerciale
              </Button>
            </Card>
          ))}
        </Grid>
      )}
    </>
  );

  return (
    <Container maxWidth="1200px" padding="2rem">
      <Header marginBottom="2rem">
        <Title level={1}>Gestione Posizioni Libere</Title>
      </Header>

      <ModernTabsContainer>
        <ModernTab 
          active={activeTab === 'vacancies'} 
          onClick={() => { setActiveTab('vacancies'); setCurrentPage(1); }}
          icon={<span>📋</span>}
        >
          Posizioni Libere
        </ModernTab>
        {canViewUnassigned && (
          <ModernTab
            active={activeTab === 'unassigned'}
            onClick={() => { setActiveTab('unassigned'); setCurrentPage(1); }}
            icon={<span>👤</span>}
          >
            Contatti Non Assegnati
          </ModernTab>
        )}
      </ModernTabsContainer>

      {activeTab === 'unassigned' && canViewUnassigned ? renderUnassignedTab() : renderVacanciesTab()}

      <Pagination marginTop="2rem">
        <PaginationButton
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Precedente
        </PaginationButton>
        <PaginationInfo>
          Pagina {currentPage} di {totalPages}
        </PaginationInfo>
        <PaginationButton
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Successivo
        </PaginationButton>
      </Pagination>

      {/* Assign Contact Modal */}
      <ModalOverlay isOpen={showAssignModal}>
        <ModalContainer size="large">
          <ModalHeader>
            <ModalTitle>Assegna posizione</ModalTitle>
            <ModalCloseButton onClick={() => setShowAssignModal(false)}>
              ×
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            {selectedVacancy && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ marginTop: '1rem' }}>
                  <Text style={{ fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                    <strong>Azienda:</strong> {selectedVacancy.company.name}
                  </Text>
                  <Text style={{ fontWeight: '600', color: '#374151' }}>
                    <strong>Posizione:</strong> {selectedVacancy.vacancy.role}
                  </Text>
                </div>
              </div>
            )}
            
            <URLImport
              profileUrl={profileUrl}
              onProfileUrlChange={setProfileUrl}
              onSuccess={() => {
                setShowAssignModal(false);
                setProfileUrl('');
                fetchVacancies();
              }}
              onCancel={() => setShowAssignModal(false)}
              companyId={selectedVacancy?.company._id}
              vacantRole={selectedVacancy?.vacancy.role}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
            />
          </ModalBody>
          {/* Action buttons are provided by URLImport to avoid duplicates */}
        </ModalContainer>
      </ModalOverlay>

      {/* Sales Rep Assignment Modal */}
      <ModalOverlay isOpen={showSalesRepModal}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Assegna Commerciale</ModalTitle>
            <ModalCloseButton onClick={() => setShowSalesRepModal(false)}>
              ×
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            {selectedContact && (
              <div style={{ marginBottom: '1rem' }}>
                <Text><strong>Contatto:</strong> {selectedContact.first_name} {selectedContact.last_name}</Text>
                {selectedContact.company_id && (
                  <Text><strong>Azienda:</strong> {selectedContact.company_id.name}</Text>
                )}
              </div>
            )}
            
            <FormGroup>
              <FormLabel>Cerca Commerciali</FormLabel>
              <SearchInput
                placeholder="Cerca per nome o email..."
                value={salesRepSearch}
                onChange={(e) => setSalesRepSearch(e.target.value)}
              />
            </FormGroup>
            
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {filteredUsers.map((user) => (
                <Card
                  key={user._id}
                  padding="0.75rem"
                  margin="0 0 0.5rem 0"
                  onClick={() => setSelectedSalesRep(user)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedSalesRep?._id === user._id ? '#f0f9ff' : 'white',
                    borderColor: selectedSalesRep?._id === user._id ? '#0ea5e9' : '#e5e7eb'
                  }}
                >
                  <Text><strong>{user.firstName} {user.lastName}</strong></Text>
                  <Text variant="body2" color="#6b7280">{user.email} • {user.role}</Text>
                </Card>
              ))}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setShowSalesRepModal(false)}>
              Annulla
            </Button>
            <Button
              variant="primary"
              onClick={handleAssignSalesRepToContact}
              disabled={!selectedSalesRep}
            >
              Assegna
            </Button>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Scraper Progress Modal */}
      <ModalOverlay isOpen={showScraperProgress}>
        <ModalContainer size="large">
          <ModalHeader>
            <ModalTitle>Progresso Scraper Profilo</ModalTitle>
            <ModalCloseButton onClick={() => setShowScraperProgress(false)}>
              ×
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Card padding="1rem" margin="0 0 1rem 0">{/* variant="light" is not supported */}
              <FlexBox justifyContent="space-between" alignItems="center" margin="0 0 0.5rem 0">
                <Text variant="body2"><strong>Stato Connessione:</strong></Text>
                <Badge variant={scraperStatus.isConnected ? 'positivo' : 'negativo'}>
                  {scraperStatus.isConnected ? 'Connesso' : 'Disconnesso'}
                </Badge>
              </FlexBox>
              <FlexBox justifyContent="space-between" alignItems="center">
                <Text variant="body2"><strong>Stato Scraper:</strong></Text>
                <Badge variant={scraperStatus.isRunning ? 'positivo' : 'default'}>
                  {scraperStatus.isRunning ? 'In Esecuzione' : 'Fermato'}
                </Badge>
              </FlexBox>
            </Card>

            {scraperStatus.totalCount > 0 && (
              <div style={{ margin: '1rem 0' }}>
                <ProgressContainer>
                  <ProgressLabel>
                    {scraperStatus.processedCount} di {scraperStatus.totalCount} elaborati
                  </ProgressLabel>
                  <ProgressTrack>
                    <ProgressFill 
                      style={{ 
                        width: `${(scraperStatus.processedCount / scraperStatus.totalCount) * 100}%` 
                      }} 
                    />
                  </ProgressTrack>
                </ProgressContainer>
              </div>
            )}

            {scraperStatus.currentContactData && (
              <Card padding="1rem" margin="0 0 1rem 0">
                <Title level={4} margin="0 0 0.75rem 0">Contatto Corrente</Title>
                <Grid columns="repeat(auto-fit, minmax(200px, 1fr))" gap="0.75rem">
                  {Object.entries(scraperStatus.currentContactData).map(([key, value]) => (
                    <div key={key}>
                      <Text variant="caption" color="#6b7280" transform="uppercase" style={{ letterSpacing: '0.05em' }}>
                        {key.replace(/_/g, ' ')}
                      </Text>
                      <Text variant="body2" margin="0.125rem 0 0 0">
                        {String(value) || 'N/A'}
                      </Text>
                    </div>
                  ))}
                </Grid>
              </Card>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="danger"
              onClick={stopScraper}
              disabled={!scraperStatus.isRunning}
            >
              Ferma Scraper
            </Button>
            <Button variant="secondary" onClick={() => setShowScraperProgress(false)}>
              Chiudi
            </Button>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </Container>
  );
};

export default VacanciesManagement;
